'use client';

import { useMemo } from 'react';
import { usePathname } from 'next/navigation';
import { LocalizedLink } from './LocalizedLink';
import { useLocaleFromPath } from '@/hooks/useLocaleFromPath';
import { getPathForLocale } from '@/lib/i18n';
import { getCanonicalSiteOrigin } from '@/lib/site-url';

type Crumb = {
  label: string;
  href?: string;
};

type BreadcrumbsProps = {
  items: Crumb[];
  /** Truncate the current (last) crumb on small screens. */
  truncateCurrent?: boolean;
};

export function Breadcrumbs({ items, truncateCurrent = false }: BreadcrumbsProps) {
  const pathname = usePathname();
  const locale = useLocaleFromPath();
  const siteUrl = getCanonicalSiteOrigin();

  const jsonLd = useMemo(() => {
    const normalize = (href: string) => {
      if (!href) return '/';
      if (href.startsWith('http://') || href.startsWith('https://')) return href;
      const localized = getPathForLocale(locale, href);
      return `${siteUrl}${localized}`;
    };
    const currentUrl = `${siteUrl}${pathname || getPathForLocale(locale, '/')}`;

    return {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: items.map((item, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: item.label,
        item: item.href ? normalize(item.href) : currentUrl,
      })),
    };
  }, [items, locale, pathname, siteUrl]);

  return (
    <>
      <nav className="text-sm text-slate-400">
        <ol className="flex flex-wrap items-center gap-2">
          {items.map((item, index) => (
            <li key={`${item.label}-${index}`} className="flex items-center gap-2">
              {item.href ? (
                <LocalizedLink href={item.href} className="hover:text-white">
                  {item.label}
                </LocalizedLink>
              ) : (
                <span
                  className={
                    truncateCurrent && index === items.length - 1
                      ? 'line-clamp-1 max-w-[10rem] text-white sm:max-w-xs md:max-w-md lg:max-w-none'
                      : 'text-white'
                  }
                  title={truncateCurrent && index === items.length - 1 ? item.label : undefined}
                >
                  {item.label}
                </span>
              )}
              {index < items.length - 1 && <span>/</span>}
            </li>
          ))}
        </ol>
      </nav>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
    </>
  );
}
